import { Product } from "~/types/product";
import { SelectedVariant, Variant } from "~/types/variant";
import ColorSwatch from "~/components/ColorSwatch";
import { useMemo, useState } from "react";
import useBreakpoints from "~/hooks/useBreakpoints";
import {
  Button,
  Dialog,
  DialogTrigger,
  Heading,
  Modal,
} from "react-aria-components";
import ColorRadioField from "~/components/ProductColorSelector/ColorRadioField";
import { toCapitalized } from "~/utils";
import ColorSelectField from "./ColorSelectField";
import { useLocation } from "react-router-dom";

const IndicationText: React.FC<{
  selectors: ColorSelector[];
  hideCloseoutMessage: boolean;
}> = ({ selectors, hideCloseoutMessage }) => {
  const dropshipSelectors = selectors.filter((s) => s.isDropship);
  const nonDropshipCloseoutSelectors = selectors.filter(
    (s) => s.isCloseout && !s.isDropship,
  );

  if (nonDropshipCloseoutSelectors.length + dropshipSelectors.length === 0)
    return null;

  return (
    <p className="ml-0.5 text-sm font-bold text-red100">
      {nonDropshipCloseoutSelectors.length > 0 && !hideCloseoutMessage ? (
        <span className="product-color-selector__closeout-indication mb-1 block text-red100">
          Red border indicates select sizes are closeout items.
        </span>
      ) : null}
      {dropshipSelectors.length > 0 ? (
        <span className="block text-blue">
          Blue border indicates select sizes and colors are drop ship items and
          are shipped directly from the manufacturer.
        </span>
      ) : null}
    </p>
  );
};

export type ColorSelector = {
  id: string;
  name: string;
  hexCodes: string[];
  colorImage?: string | null;
  productImage?: string | null;
  isCloseout: boolean;
  isDropship: boolean;
  startingPrice: number;
};

type Props = {
  product: Product;
  selectedVariant: SelectedVariant;
  setSelectedVariant: (variant: Variant) => void;
};

const ProductColorSelector: React.FC<Props> = ({
  product,
  selectedVariant,
  setSelectedVariant,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isMobile, isTablet } = useBreakpoints();
  const location = useLocation();

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = event.target.value;
    const variant = product.variants.find((variant) => variant.id === id);

    if (!variant) return;

    setSelectedVariant(variant);

    if (!isModalOpen) return;

    setIsModalOpen(false);
  };

  const handleSelectChange = (id: string | number) => {
    const variantId = id.toString().replace("popular-", "");

    const variant = product.variants.find(
      (variant) => variant.id === variantId,
    );

    if (!variant) return;

    setSelectedVariant(variant);
  };

  const selectors: ColorSelector[] = useMemo(
    () =>
      product.colors.flatMap((colorVariant) => {
        const id =
          colorVariant.color === selectedVariant.color
            ? selectedVariant.id
            : product.variants.find((v) => v.color === colorVariant.color)?.id;

        if (!id) return [];

        return {
          id,
          name: toCapitalized(colorVariant.color),
          startingPrice: colorVariant.startingPrice,
          hexCodes: colorVariant.swatch.hexCodes,
          colorImage: colorVariant.swatch.url,
          isCloseout: colorVariant.isCloseout,
          isDropship: colorVariant.isDropship,
          productImage:
            colorVariant.imageUrls.front?.cell ||
            colorVariant.imageUrls.side?.cell ||
            colorVariant.imageUrls.back?.cell,
        };
      }),
    [product.variants, selectedVariant],
  );

  const popularColorSelectors = useMemo(() => {
    return product.popularColors
      .map((color) =>
        selectors.find((selector) => selector.name.toUpperCase() === color),
      )
      .filter((selector) => selector !== undefined);
  }, [selectors, product.popularColors]);

  if (isMobile || isTablet) {
    return (
      <DialogTrigger>
        <div className="flex flex-col gap-4 lg:hidden">
          <Button
            aria-label="Select Color"
            onPress={() => setIsModalOpen(true)}
            className="inline-block h-9 whitespace-nowrap rounded-[3px] border-none bg-blue800 px-[min(100px,8ch)] text-center text-sm uppercase text-white hover:bg-blue200"
          >
            Select Color
          </Button>
          <div
            data-testid="selectedColor"
            className="flex items-center justify-center gap-2.5 text-sm text-blue900"
          >
            <span>Selected:</span>
            <ColorSwatch
              isActive={false}
              image={selectedVariant.swatch.url}
              hexCodes={selectedVariant.swatch.hexCodes}
              hasHover={false}
            />{" "}
            <span>{toCapitalized(selectedVariant.color)}</span>
          </div>
        </div>
        <Modal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          className="fixed left-0 top-0 z-[10000] h-full w-full bg-white300"
        >
          <Dialog
            aria-label="Color Selectors Dialog"
            className="h-full overflow-y-scroll pb-20"
          >
            <header className="relative mb-6 flex min-h-[120px] flex-col justify-end bg-blue px-4 py-1.5">
              <Heading className="text-center font-serif text-[1.5625rem] font-light text-white [text-shadow:_1.5px_1.5px_#494949]">
                Select Color
              </Heading>
              <Button
                aria-label="Close"
                onPress={() => setIsModalOpen(false)}
                className="absolute right-3 top-2.5 h-[50px] w-[50px] bg-[url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMTk0LjYgMzMzLjkiIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDE5NC42IDMzMy45OyIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+PHN0eWxlIHR5cGU9InRleHQvY3NzIj4uc3Qwe2ZpbGw6I0ZGRkZGRjt9PC9zdHlsZT48cG9seWdvbiBjbGFzcz0ic3QwIiBwb2ludHM9IjEwOS40LDE2Mi44IDE5MS41LDAuNSAxNjkuMywwLjUgOTguMiwxNDAuNyAyNS40LDAuNSAyLjQsMC41IDg2LjUsMTYyLjggMCwzMzMuOSAyMi4yLDMzMy45IDk3LjYsMTg1IDE3MS44LDMzMy45IDE5NC42LDMzMy45ICIvPjwvc3ZnPg==)] bg-right-top bg-no-repeat [background-size:_14px_21px]"
              />
            </header>
            {popularColorSelectors.length > 0 ? (
              <fieldset
                aria-label="Popular Colors"
                className="mb-8 flex flex-wrap justify-center gap-x-3 gap-y-4 px-4"
              >
                <Heading className="mb-4 w-full text-center font-serif text-[22px] font-bold text-blue900">
                  Popular Colors
                </Heading>

                {popularColorSelectors.map((selector, index) => (
                  <ColorRadioField
                    key={`popular-${selector.id}-${index}`}
                    colorSelector={selector}
                    isActive={selector.id === selectedVariant.id}
                    onChange={handleRadioChange}
                    idModifier="popular"
                  />
                ))}
              </fieldset>
            ) : null}
            <fieldset
              aria-label="All Colors"
              className="flex flex-wrap justify-center gap-x-3 gap-y-4 px-4"
            >
              <Heading className="mb-4 w-full text-center font-serif text-[22px] font-bold text-blue900">
                All Colors
              </Heading>
              {selectors.map((selector, index) => (
                <ColorRadioField
                  key={index}
                  colorSelector={selector}
                  isActive={selector.id === selectedVariant.id}
                  onChange={handleRadioChange}
                />
              ))}
            </fieldset>
            <div className="ml-8 mt-8 max-w-[35ch]">
              <IndicationText
                selectors={selectors}
                hideCloseoutMessage={location.pathname.includes("closeout")}
              />
            </div>
          </Dialog>
        </Modal>
      </DialogTrigger>
    );
  }

  return (
    <div className="hidden w-full flex-col gap-5 lg:flex">
      <div className="flex items-end gap-6">
        <ColorSelectField
          colorSelectors={selectors}
          popularColorSelectors={popularColorSelectors}
          selectedOptionId={selectedVariant.id}
          onChange={handleSelectChange}
        />
        {popularColorSelectors.length > 0 ? (
          <fieldset
            aria-label="Popular Colors"
            className="flex max-w-[250px] flex-wrap items-center justify-start gap-x-[12px] gap-y-2"
          >
            <Heading
              level={4}
              className="font-serif text-sm font-bold text-blue900"
            >
              Popular Colors
            </Heading>
            {popularColorSelectors.map((selector, index) => (
              <ColorRadioField
                key={index}
                colorSelector={selector}
                isActive={selector.id === selectedVariant.id}
                onChange={handleRadioChange}
                idModifier="popular"
              />
            ))}
          </fieldset>
        ) : null}
      </div>
      <fieldset
        aria-label="All Colors"
        className="ml-0.5 flex flex-wrap items-start justify-start gap-2.5"
      >
        {selectors.map((selector, index) => (
          <ColorRadioField
            key={index}
            colorSelector={selector}
            isActive={selector.id === selectedVariant.id}
            onChange={handleRadioChange}
            hasFlyout={true}
          />
        ))}
      </fieldset>
      <IndicationText
        selectors={selectors}
        hideCloseoutMessage={location.pathname.includes("closeout")}
      />
    </div>
  );
};

export default ProductColorSelector;
